import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Image, Music, Film, Download, Replace, RefreshCw, X, ZoomIn, FolderOpen, Upload, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/utils/cn';
import { listAssets, replaceAsset, replaceAssetFromUrl, type AssetItem, type CategorizedAssetItem } from '@/api/modules/assets';
import { AssetImage } from '@/components/asset-image';
import { AssetLibraryModal } from '@/components/asset-library-modal';

type TabKey = 'image' | 'audio' | 'animation';

const SOURCE_BADGE: Record<string, { label: string; className: string }> = {
  ai_generated: { label: 'AI', className: 'bg-blue-500/80' },
  user_upload: { label: '上传', className: 'bg-emerald-500/80' },
  // 手动替换过的素材：与 AI 同色蓝标，标注「手动」（与弹窗「手动上传」语义一致）
  user_replaced: { label: '手动', className: 'bg-blue-500/80' },
  public_library: { label: '库', className: 'bg-amber-500/80' },
  my_library: { label: '我的', className: 'bg-violet-500/80' },
};

interface MaterialPanelProps {
  /** 传入则只展示该会话素材（本项目素材面板）。 */
  sessionId?: string;
  /** 外部触发刷新：值变化时重新拉取（用于 asset:ready 实时刷新）。 */
  refreshKey?: number;
  /** 替换图片后产物预览地址更新（用于刷新预览页面）。BUG-20260810-060: newVersionId=替换产生的新版本 id，父级据此重载版本列表并切换 */
  onPreviewUpdated?: (previewUrl: string, newVersionId?: string) => void;
  /** 版本素材快照 JSON（Playable.assetManifest）——传入时展示该版本用到的素材，并允许基于该版本替换 */
  versionSnapshot?: string;
  /** 快照版本标签（如 V2） */
  snapshotLabel?: string;
  /** 基准变体 ID（替换时传给后端定位基线产物） */
  basePlayableId?: string;
}

export function MaterialPanel({ sessionId, refreshKey, onPreviewUpdated, versionSnapshot, snapshotLabel, basePlayableId }: MaterialPanelProps): JSX.Element {
  const [tab, setTab] = useState<TabKey>('image');
  const [assets, setAssets] = useState<AssetItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [slowLoading, setSlowLoading] = useState(false);
  /** 同槽位多版本展开态（key=slotName） */
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  /** 素材大图预览 */
  const [previewAsset, setPreviewAsset] = useState<AssetItem | null>(null);
  /** 替换弹窗状态：当前待替换素材 + 替换方式选择弹窗 + 资产库弹窗 */
  const [replaceTarget, setReplaceTarget] = useState<AssetItem | null>(null);
  const [showReplacePicker, setShowReplacePicker] = useState(false);
  const [showLibModal, setShowLibModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // BUG-20260810-056: 版本素材快照模式——切换旧版本时展示该版本生成时的素材清单。
  // 历史版本允许替换；后端会以 basePlayableId fork 出新版本，不修改原版本文件。
  const snapshotItems = useMemo<AssetItem[] | null>(() => {
    if (!versionSnapshot) return null;
    try {
      const arr = JSON.parse(versionSnapshot) as Array<Record<string, any>>;
      if (!Array.isArray(arr)) return null;
      return arr.map((r, i) => ({
        id: r.fileName || `snap-${i}`,
        url: r.url,
        fileName: r.fileName,
        slotName: r.slotName,
        category: r.category,
        resourceType: (r.resourceType ?? 'image') as AssetItem['resourceType'],
        type: r.resourceType === 'audio' ? ('audio' as const) : ('image' as const),
        source: r.source as AssetItem['source'],
        width: r.width,
        height: r.height,
        createdAt: r.createdAt,
      }));
    } catch { return null; }
  }, [versionSnapshot]);

  const tabs: Array<{ key: TabKey; label: string; icon: typeof Image }> = [
    { key: 'image', label: '图片', icon: Image },
    { key: 'audio', label: '音频', icon: Music },
    { key: 'animation', label: '动画', icon: Film },
  ];

  const fetchAssets = useCallback(async () => {
    // BUG-20260810-056: 快照模式不走网络，直接按 tab 过滤快照素材
    if (snapshotItems) {
      setAssets(snapshotItems.filter(a => (a.resourceType ?? 'image') === tab));
      return;
    }
    setLoading(true);
    try {
      setAssets(await listAssets({ resourceType: tab, sessionId }));
    } catch { setAssets([]); }
    finally { setLoading(false); }
  }, [tab, sessionId, snapshotItems]);

  useEffect(() => { fetchAssets(); }, [fetchAssets, refreshKey]);

  // 慢加载提示：loading 超 6 秒给出可感知的重试出口（正常接口毫秒级，
  // 超过即大概率排队/挂起，由 listAssets 10s 超时兼兜底终结）。
  useEffect(() => {
    if (!loading) { setSlowLoading(false); return; }
    const t = setTimeout(() => setSlowLoading(true), 6000);
    return () => clearTimeout(t);
  }, [loading]);

  const handleReplace = async (id: string, file: File): Promise<void> => {
    try {
      const target = replaceTarget;
      const res = await replaceAsset(id, file, basePlayableId, {
        slotName: target?.slotName,
        sessionId,
      });
      toast.success('替换成功');
      // 替换已重新注入试玩产物，更新预览页面（多文件/单文件产物均支持）
      if (res.previewUrl) onPreviewUpdated?.(res.previewUrl, res.newVersion?.id);
      fetchAssets();
    } catch { toast.error('替换失败'); }
  };

  /** 打开替换方式弹窗（资产库 / 本地上传），参考对话框附件上传交互。 */
  const openReplace = (a: AssetItem): void => {
    setReplaceTarget(a);
    setShowReplacePicker(true);
  };

  /** 资产库确认选择 → 用 CDN 资源替换（后端下载，规避浏览器跨域）。 */
  const handleLibPicked = async (chosen: CategorizedAssetItem[]): Promise<void> => {
    const item = chosen[0];
    const target = replaceTarget;
    if (!item || !target) return;
    // 类型限制：音频槽位只允许音频资源；图片/动画槽位不允许音频资源
    const isAudioTarget = target.resourceType === 'audio';
    if (isAudioTarget && item.type !== 'audio') { toast.error('该槽位是音频素材，请选择音频资源'); return; }
    if (!isAudioTarget && item.type === 'audio') { toast.error('该槽位是图片素材，请选择图片资源'); return; }
    try {
      const res = await replaceAssetFromUrl(target.id, item.url, basePlayableId, {
        slotName: target.slotName,
        sessionId,
      });
      toast.success('替换成功');
      if (res.previewUrl) onPreviewUpdated?.(res.previewUrl, res.newVersion?.id);
      fetchAssets();
    } catch { toast.error('替换失败'); }
    setReplaceTarget(null);
  };

  const handleCopyName = (name: string) => {
    navigator.clipboard.writeText(name).then(() => toast.success(`已复制：${name}`));
  };

  // 按槽位分组：产物实际采用每槽位最新一张（assemble 从 state.generatedAssets 取当轮生成图，
  // QA 重试/装填轮会重新生成并新增 asset 记录）→ main 必须取最新，否则面板显示首轮旧图、
  // 与产物内嵌图不一致（「素材对不上」）。
  // DO-NOT-REVERT: BUG-20260806-035 同槽位多版本时禁止按旧到新取首张
  const groupList = (() => {
    const groups = new Map<string, AssetItem[]>();
    for (const a of assets) {
      const key = a.slotName || a.category || a.id;
      const arr = groups.get(key) ?? [];
      arr.push(a);
      groups.set(key, arr);
    }
    return [...groups.entries()].map(([key, arr]) => {
      const sorted = [...arr].sort((x, y) =>
        String(y.createdAt ?? y.fileName ?? '').localeCompare(String(x.createdAt ?? x.fileName ?? '')),
      );
      return { key, main: sorted[0], versions: sorted };
    });
  })();

  return (
    <div>
      {/* 快照提示独立成行：与 tab 同行时，窄面板（previewW 可拖窄）会把
          「图片/音频/动画」tab 挤压折行变形（tab 加 shrink-0 + nowrap 防压缩） */}
      <div className={cn('flex items-center gap-1', snapshotItems ? 'mb-1.5' : 'mb-2')}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={cn('flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md px-2.5 py-1 text-xs', tab === t.key ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground')}>
            <t.icon size={12} />{t.label}
          </button>
        ))}
        {!snapshotItems && sessionId && (
          <div className="ml-auto flex items-center gap-1">
            {/* DO-NOT-REVERT: BUG-20260805-014 — 打包下载按钮与 onPreviewUpdated 预览刷新，禁止移除；
                「一键替换」按钮已于 2026-08-05 按用户要求移除（单张替换已自动刷新预览） */}
            {/* 一键打包下载所有图片为 zip */}
            <a href={`/api/assets/download-all?sessionId=${sessionId}`} download
              className="icon-btn flex items-center gap-1 rounded-md border border-border/50 px-2 py-1 text-[11px] text-foreground/85 hover:border-primary/50 hover:text-primary"
              title="下载本项目所有图片为 zip 包">
              <Download size={12} />打包下载
            </a>
          </div>
        )}
      </div>

      {snapshotItems && (
        <div className="mb-2 rounded-md bg-amber-500/15 px-2 py-1 text-[11px] text-amber-600 dark:text-amber-400">
          {snapshotLabel ?? '旧版本'} 素材快照 · 可替换并生成新版本
        </div>
      )}

      {loading ? (
        <div>
          <div className="grid grid-cols-3 gap-2">
            {Array.from({ length: 6 }).map((_, i) => (<div key={i} className="aspect-square shimmer rounded-lg bg-muted" />))}
          </div>
          {slowLoading && (
            <div className="mt-3 flex items-center justify-center gap-2 text-xs text-muted-foreground">
              <span>加载较慢…</span>
              <button onClick={() => fetchAssets()} className="rounded-md bg-primary/15 px-2 py-1 text-primary hover:bg-primary/25">点击重试</button>
            </div>
          )}
        </div>
      ) : groupList.length === 0 ? (
        <div className="py-8 text-center text-xs text-muted-foreground">暂无素材</div>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {groupList.map(g => {
            const isExp = !!expanded[g.key];
            const shown = isExp ? g.versions : [g.main];
            return shown.map((a, vi) => {
              const badge = SOURCE_BADGE[a.source ?? 'ai_generated'];
              return (
                <div key={a.id} className="group relative overflow-hidden rounded-lg bg-muted">
                  {a.error ? (
                    <div className="flex aspect-square flex-col items-center justify-center gap-1 bg-red-500/5 p-2 text-center">
                      <RefreshCw size={16} className="text-red-400" />
                      <span className="text-[9px] text-red-400/90">{a.errorMessage || '失败'}</span>
                    </div>
                  ) : a.resourceType === 'audio' ? (
                    <div className="flex aspect-square flex-col items-center justify-center gap-1 bg-muted/40 p-1">
                      <Music size={18} className="text-muted-foreground/50" />
                      <audio controls src={a.url} className="w-full" style={{ height: 22 }} />
                    </div>
                  ) : (
                    <button className="relative w-full cursor-zoom-in" onClick={() => setPreviewAsset(a)} title="点击预览大图">
                      <AssetImage src={a.url} alt={a.name} className="aspect-square w-full" />
                      <ZoomIn size={12} className="absolute right-1 top-1 text-white/70 opacity-0 transition-opacity group-hover:opacity-100" />
                    </button>
                  )}
                  {badge && !a.error && (
                    <span className={cn('absolute left-1 top-1 rounded px-1 py-px text-[8px] font-medium text-white', badge.className)}>{badge.label}</span>
                  )}
                  {/* 多版本角标：×N 点击展开全部版本；展开后显示 v序号，再点收起 */}
                  {g.versions.length > 1 && !a.error && (
                    <button
                      onClick={() => setExpanded(p => ({ ...p, [g.key]: !isExp }))}
                      title={isExp ? '收起（游戏采用最新版）' : `共 ${g.versions.length} 个版本，游戏采用最新版，点击展开`}
                      className="absolute right-1 top-1 rounded bg-black/60 px-1 py-px text-[8px] font-medium text-white hover:bg-black/80"
                    >
                      {isExp ? `v${vi + 1}${vi === 0 ? '·采用' : ''}` : `×${g.versions.length}`}
                    </button>
                  )}
                  {!a.error && (
                    <div className="absolute inset-x-0 bottom-0 flex items-center gap-0.5 bg-gradient-to-t from-black/60 p-1 opacity-0 transition-opacity group-hover:opacity-100">
                      {/* 槽位名（左）+ 点击复制 ——与工作流素材面板槽位语义对齐 */}
                      {(a.slotName || a.name) && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleCopyName(a.slotName || a.name || ''); }}
                          title="点击复制文件名"
                          className="mr-auto max-w-[55%] truncate rounded px-1 py-px text-left text-[8px] text-white/90 hover:text-white hover:bg-white/15 cursor-pointer transition-colors"
                        >
                          {a.slotName || a.name}
                        </button>
                      )}
                      {/* 下载/替换按钮：默认亮边框+浅色底，深色图上也能一眼看到（icon-btn 默认深灰边框对比度不足） */}
                      <a href={snapshotItems ? a.url : `/api/assets/${a.id}/download`} download className="icon-btn rounded border-white/70 bg-white/10 p-0.5 text-white/90 hover:bg-white/25" title="下载"><Download size={12} /></a>
                      {/* 替换：弹窗选择来源（资产库 / 本地上传），按当前素材类型限制文件类型 */}
                      <button type="button" onClick={() => openReplace(a)} className="icon-btn cursor-pointer rounded border-white/70 bg-white/10 p-0.5 text-white/90 hover:bg-white/25" title="替换">
                        <Replace size={12} />
                      </button>
                    </div>
                  )}
                </div>
              );
            });
          })}
        </div>
      )}

      {/* 素材大图预览弹层 */}
      {previewAsset && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6" onClick={() => setPreviewAsset(null)}>
          <div className="max-h-full w-[360px] overflow-y-auto rounded-xl border border-border/50 bg-card p-3" onClick={(e) => e.stopPropagation()}>
            {previewAsset.url ? (
              <img src={previewAsset.url} alt={previewAsset.slotName ?? previewAsset.name ?? ''} className="w-full rounded-lg bg-muted object-contain" />
            ) : (
              <div className="flex aspect-square items-center justify-center bg-muted/50 rounded-lg">
                <RefreshCw size={32} className="text-muted-foreground/40" />
              </div>
            )}
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <button
                onClick={() => handleCopyName(previewAsset.slotName || previewAsset.name || previewAsset.fileName || previewAsset.id?.slice(0, 8) || '')}
                title="点击复制文件名"
                className="group/copy flex items-center gap-1 rounded px-1 -mx-1 hover:bg-primary/10 transition-colors cursor-pointer"
              >
                <span className="font-mono text-xs text-foreground">{previewAsset.slotName || previewAsset.name || previewAsset.fileName || previewAsset.id?.slice(0, 8)}</span>
                <Copy size={11} className="text-muted-foreground/50 opacity-0 group-hover/copy:opacity-100 transition-opacity" />
              </button>
              {previewAsset.category && (
                <span className="rounded bg-primary/10 px-1.5 py-px text-[10px] text-primary">{previewAsset.category}</span>
              )}
              {previewAsset.width && previewAsset.height && (
                <span className="text-[10px] text-muted-foreground">{previewAsset.width}×{previewAsset.height}</span>
              )}
              {previewAsset.fileSize != null && (
                <span className="text-[10px] text-muted-foreground">{previewAsset.fileSize > 1024 ? `${(previewAsset.fileSize / 1024).toFixed(1)} KB` : `${previewAsset.fileSize} B`}</span>
              )}
              {previewAsset.source && (
                <span className="ml-auto rounded bg-muted px-1.5 py-px text-[10px] text-muted-foreground">
                  {previewAsset.source === 'ai_generated' ? 'AI生成' : previewAsset.source === 'user_replaced' ? '手动上传' : previewAsset.source === 'user_upload' ? '上传' : previewAsset.source}
                </span>
              )}
            </div>
            <button className="mt-3 w-full rounded-md border border-border/50 py-1 text-xs text-muted-foreground hover:text-foreground" onClick={() => setPreviewAsset(null)}>关闭</button>
          </div>
        </div>
      )}

      {/* 本地上传替换：按素材类型限制 accept（图片/动画 → image/*，音频 → audio/*） */}
      <input ref={fileInputRef} type="file" className="hidden"
        accept={replaceTarget?.resourceType === 'audio' ? 'audio/*' : 'image/*'}
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          setShowReplacePicker(false);
          if (f && replaceTarget) handleReplace(replaceTarget.id, f);
        }} />

      {/* 替换方式选择弹窗（资产库 / 本地上传），交互参考对话框附件上传弹窗 */}
      {showReplacePicker && replaceTarget && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowReplacePicker(false)} />
          <div className="glass-card relative z-10 flex w-full max-w-md flex-col p-6 animate-in fade-in zoom-in-95 duration-200">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-base font-semibold text-foreground">替换素材</h3>
              <button onClick={() => setShowReplacePicker(false)} className="icon-btn rounded p-1"><X size={16} /></button>
            </div>
            <p className="mb-4 text-xs text-muted-foreground">
              替换「{replaceTarget.slotName || replaceTarget.name || replaceTarget.fileName || replaceTarget.id?.slice(0, 8)}」
              ——{replaceTarget.resourceType === 'audio' ? '仅支持音频文件' : '仅支持图片文件'}
            </p>
            <div className="grid grid-cols-2 gap-4">
              {/* 资产库 */}
              <button
                onClick={() => { setShowReplacePicker(false); setShowLibModal(true); }}
                className="flex flex-col items-center gap-3 rounded-xl border border-border/60 bg-card/50 p-6 transition-all hover:border-primary/50 hover:bg-primary/5"
              >
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
                  <FolderOpen size={28} className="text-primary" />
                </div>
                <div className="text-center">
                  <div className="text-sm font-medium text-foreground">资产库</div>
                  <div className="mt-0.5 text-[11px] text-muted-foreground">从资产库选择资源</div>
                </div>
              </button>
              {/* 本地上传 */}
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex flex-col items-center gap-3 rounded-xl border border-border/60 bg-card/50 p-6 transition-all hover:border-primary/50 hover:bg-primary/5"
              >
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-neon-cyan/10">
                  <Upload size={28} className="text-neon-cyan" />
                </div>
                <div className="text-center">
                  <div className="text-sm font-medium text-foreground">本地上传</div>
                  <div className="mt-0.5 text-[11px] text-muted-foreground">{replaceTarget.resourceType === 'audio' ? '上传音频文件' : '上传图片文件'}</div>
                </div>
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* 资产库选择替换（单选、不导入会话，按槽位类型定位初始分类） */}
      <AssetLibraryModal
        open={showLibModal}
        onOpenChange={setShowLibModal}
        single
        importOnConfirm={false}
        initialTab={replaceTarget?.resourceType === 'audio' ? 'audio' : replaceTarget?.resourceType === 'animation' ? 'animation' : 'image'}
        assetKind={replaceTarget?.resourceType === 'audio' ? 'audio' : 'image'}
        onPicked={handleLibPicked}
      />
    </div>
  );
}
